import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType } from 'yup';

import { mutations } from 'RestAPIs/rapini';

import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, DialogActions } from '@mui/material';

import { Dialog, showSnackbar } from 'components/shared';
import ValidFromValidTo from 'components/shared/ControlledFields/ControlledValidFromValidTo';

import recalculateCostCalculationExternalSchema from './recalculate-cost-calculation-external-schema';

const schema = recalculateCostCalculationExternalSchema();
type Form = InferType<typeof schema>;

export type RecalculateCostCalculationExternalProperties = {
  employmentId?: number;
  organizationalUnitId?: number;
  isOpen: boolean;
  handleClose: () => void;
};

export function RecalculateCostCalculationExternal({
  isOpen,
  handleClose,
  employmentId,
  organizationalUnitId
}) {
  const { t } = useTranslation('common');

  const defaultValues = {
    validFrom: dayjs().startOf('month'),
    validTo: dayjs().startOf('year').add(1, 'year')
  };

  const { control, handleSubmit } = useForm<Form>({
    resolver: yupResolver(schema),
    defaultValues
  });

  const { mutate: recalculate, isLoading: isRecalculateLoading } =
    mutations.useRecalculateCostCalculationExternalSurcharge({
      onSuccess: () => {
        handleClose();
      },
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      }
    });

  const onSubmit = async (data: Form) => {
    recalculate({
      startDate: data.validFrom.format('YYYY-MM-DD'),
      endDate: data.validTo.format('YYYY-MM-DD'),
      employmentId,
      organizationalUnitId
    });
  };

  return (
    <Dialog
      open={isOpen}
      title={t('recalculateCostCalculationExternal')}
      handleClose={handleClose}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Box mb={2}>
          <Alert severity="warning" variant="outlined">
            {t(
              employmentId
                ? 'recalculateCostCalculationExternalEmployeeInfo'
                : 'recalculateCostCalculationExternalOrganizationalUnitInfo'
            )}
            <br />
            {t('recalculateCostCalculationExternalGenericInfo')}
          </Alert>
        </Box>
        <ValidFromValidTo control={control} />
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="contained">
            {t('cancel')}
          </Button>
          <LoadingButton
            loading={isRecalculateLoading}
            type="submit"
            color="primary"
            variant="contained"
          >
            {t('recalculate')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
